import React, { useCallback } from "react";
import RasterLayer from "./RasterLayer";
import styles from "./LayersEditor.module.css";
import { produce } from "immer";
import { RasterMapLayerEditAction } from "data/types";
import { moveArrayItem } from "data/utils";

type Props = {
    onMapEdit: (map) => void,
    map: RasterMapResultDTO,
};

const LayersEditor = ({ map, onMapEdit }: Props) => {
    const removeLayer = useCallback(
        (layer) => {
            const idx = map.layers.findIndex((i) => i.id === layer.id);
            onMapEdit(
                produce(map, (draft) => {
                    draft.layers[idx].editAction =
                        RasterMapLayerEditAction.delete;
                })
            );
        },
        [map, onMapEdit]
    );

    const renameLayer = useCallback(
        (layer, newName) => {
            const idx = map.layers.findIndex((i) => i.id === layer.id);
            onMapEdit(
                produce(map, (draft) => {
                    draft.layers[idx].name = newName;
                    // do not change editAction for new .added layers
                    if (
                        draft.layers[idx].editAction !==
                        RasterMapLayerEditAction.add
                    )
                        draft.layers[idx].editAction =
                            RasterMapLayerEditAction.rename;
                })
            );
        },
        [map, onMapEdit]
    );

    const moveUp = useCallback(
        (layer) => {
            const idx = map.layers.findIndex((i) => i.id === layer.id);
            let target = idx - 1;
            if (idx === 0) target = map.layers.length - 1;
            onMapEdit(
                produce(map, (draft) => {
                    draft.layers = moveArrayItem(draft.layers, idx, target);
                })
            );
        },
        [map, onMapEdit]
    );

    const moveDown = useCallback(
        (layer) => {
            const idx = map.layers.findIndex((i) => i.id === layer.id);
            let target = idx + 1;
            if (target === map.layers.length) target = 0;
            onMapEdit(
                produce(map, (draft) => {
                    draft.layers = moveArrayItem(draft.layers, idx, target);
                })
            );
        },
        [map, onMapEdit]
    );

    return (
        <div className={styles.wrapper}>
            {map.layers
                .filter((m) => m.editAction !== RasterMapLayerEditAction.delete)
                .map((layer, i) => (
                    <RasterLayer
                        key={layer.id || `RasterLayer${i}`}
                        layer={layer}
                        onRemove={() => removeLayer(layer)}
                        onRename={(newName) => renameLayer(layer, newName)}
                        onMoveUp={() => moveUp(layer)}
                        onMoveDown={() => moveDown(layer)}
                    />
                ))}
        </div>
    );
};
LayersEditor.displayName = "RasterMapManager.LayersEditor";
export default LayersEditor;
